import dayjs from "dayjs";
import { useRef } from "react";

import { DEFAULT_CONTRIBUTE_AFTER_18, DEFAULT_RATE_OF_RETURN } from "@constants";
import { AccountType, BeneficiaryType, DateFormat, Flow } from "@ctypes";
import { convertDollarToCents, getHarvestAddGoalVariables, readUserFromCache } from "@utils";
import { useAddGoal } from "./useAddGoal";
import { useAddGoalWithBeneficiary } from "./useAddGoalWithBeneficiary";
import { useGoalFlow } from "./useGoalFlow";

export type PersistSidecarGoalProps = {
  accountType?: AccountType;
  ssn?: string;
  harvestStandardDeduction?: boolean;
  harvestLongTermCapitalGains?: boolean;
  harvestRecommendedFloorUsd?: number;
};

/**
 * Creates a Trump Account Sidecar goal. The sidecar contribution picked on the
 * recommended contribution route becomes the goal's recurring deposit, which is
 * why the sidecar flow never visits a deposit route.
 *
 * During onboarding the beneficiary does not exist yet, so the goal and the
 * beneficiary are created together in a single mutation — a two step create
 * could leave the account past the `!beneficiaries.length` onboarding gate with
 * no goal and no way back into the goal flow.
 */
export const usePersistSidecarGoal = () => {
  const {
    goalProps: {
      beneficiaryId,
      beneficiaryType = BeneficiaryType.Child,
      firstName,
      lastName,
      dateOfBirth,
      isFutureBeneficiary,
      accountType: accountTypeFromGoal,
      flow,
      goalName,
      goalType,
      targetYear,
      targetAmount,
      ssn: ssnFromGoal,
      trumpAccountBalanceInCents,
      trumpContributionAmountInCents,
      trumpContributionFrequency,
      expectedTaxBillWithoutSidecarInCents,
      expectedTaxBillWithSidecarInCents,
      spendableWithoutSidecarInCents,
      spendableWithSidecarInCents,
      sidecarContributionAmountInCents,
      sidecarContributionFrequency,
      incomeAt18,
      harvestStandardDeduction,
      harvestLongTermCapitalGains,
      harvestRecommendedFloorUsd,
    },
    setGoalProps,
  } = useGoalFlow();

  const [addGoal, { loading: addGoalLoading }] = useAddGoal();
  const [addGoalWithBeneficiary, { loading: addGoalWithBeneficiaryLoading }] =
    useAddGoalWithBeneficiary();

  // Onboarding success paths `replace` the current entry, so the in app back
  // arrow lands on the previous sidecar screen and the CTA can be pressed
  // again. This latch stops a second create within the same mount.
  const persistedRef = useRef(false);

  const persistSidecarGoal = async ({
    // `useGoalFlow` closes over the render time goalProps, so anything the
    // caller stages with setGoalProps in the same tick it calls this is not
    // visible here. Those values are passed in as arguments instead.
    accountType: accountTypeOverride,
    ssn,
    harvestStandardDeduction: harvestStandardDeductionOverride,
    harvestLongTermCapitalGains: harvestLongTermCapitalGainsOverride,
    harvestRecommendedFloorUsd: harvestRecommendedFloorUsdOverride,
  }: PersistSidecarGoalProps = {}) => {
    const accountType = accountTypeOverride ?? accountTypeFromGoal;

    if (!accountType) throw new Error("Missing account type");
    if (persistedRef.current) return beneficiaryId;

    // Onboarding only ever creates the first beneficiary and their first goal
    // (`getMissingFieldRoute` gates the goal flow on `!beneficiaries.length`), so
    // if one already exists this flow has run before — almost certainly a back
    // then forward resubmit. No-op rather than adding a duplicate goal, which the
    // per mount latch above cannot catch once the route remounts.
    const isOnboarding = !flow || flow === Flow.Onboarding;

    if (isOnboarding && readUserFromCache()?.beneficiaries?.length) {
      persistedRef.current = true;

      return beneficiaryId;
    }

    const harvestVariables = getHarvestAddGoalVariables({
      accountType,
      harvestStandardDeduction: harvestStandardDeductionOverride ?? harvestStandardDeduction,
      harvestLongTermCapitalGains:
        harvestLongTermCapitalGainsOverride ?? harvestLongTermCapitalGains,
      harvestRecommendedFloorUsd: harvestRecommendedFloorUsdOverride ?? harvestRecommendedFloorUsd,
    });

    const sidecarVariables = {
      accountType,
      goalName,
      goalType,
      targetYear,
      targetAmountInCents: convertDollarToCents(targetAmount),
      recurringDepositInCents: sidecarContributionAmountInCents,
      recurringDepositSchedule: sidecarContributionFrequency,
      projectedRateOfReturn: DEFAULT_RATE_OF_RETURN,
      contributionsAfter18: DEFAULT_CONTRIBUTE_AFTER_18,
      trumpAccountBalanceInCents,
      trumpContributionAmountInCents,
      trumpContributionFrequency,
      expectedTaxBillWithoutSidecarInCents,
      expectedTaxBillWithSidecarInCents,
      spendableWithoutSidecarInCents,
      spendableWithSidecarInCents,
      incomeAt18,
      ...harvestVariables,
    };

    if (beneficiaryId) {
      await addGoal({ variables: { ...sidecarVariables, beneficiaryId } });

      persistedRef.current = true;

      return beneficiaryId;
    }

    if (!firstName || !lastName) throw new Error("Missing beneficiary name");

    const { data } = await addGoalWithBeneficiary({
      variables: {
        ...sidecarVariables,
        beneficiary: {
          firstName,
          lastName,
          // A future beneficiary has no date of birth; formatting `undefined`
          // would send today instead.
          dateOfBirth:
            isFutureBeneficiary || !dateOfBirth ? null : dayjs(dateOfBirth).format(DateFormat.User),
          type: beneficiaryType,
          taxId: ssn ?? ssnFromGoal,
          incomeAt18,
        },
      },
    });

    const id = data?.addGoalWithBeneficiary?.beneficiary?.id ?? undefined;

    persistedRef.current = true;

    // Keep the new id so any re invocation adds a goal to this beneficiary
    // instead of creating a duplicate.
    if (id) setGoalProps({ beneficiaryId: id });

    return id;
  };

  return {
    persistSidecarGoal,
    loading: addGoalLoading || addGoalWithBeneficiaryLoading,
  };
};
